import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CATALOGS, DEFAULT_LOCALE, INTL_TAG, nb, type Locale, type MessageKey } from './catalogs';
import { persistLocale, readLocale } from './localePreference';

// ═══════════════════════════════════════════════════════════════════
// THE LOCALE SEAM (#310a).
//
// One provider, one hook, and nothing else for a feature to wire up. The point
// of this slice is that the NEXT surface to be translated needs no
// architecture work — it imports `useT` and uses keys.
//
// SWITCHING IS STATE, NOT A RELOAD. The locale lives in React state, so
// changing it re-renders every consumer; a reload would lose unsaved work in
// whatever the operator had open, which is a strange price for changing a
// dropdown.
//
// TWO WAYS THE LOCALE MOVES, AND THEY ARE NOT THE SAME EVENT (#310):
//
//   setLocale    somebody CHOSE this. It is written down first and shown
//                second — see `persistLocale`. Asynchronous because where it
//                is written down may be the account, which is a request.
//   adoptLocale  we LEARNED this — the signed-in account's stored preference
//                arriving after sign-in, or the browser's coming back after
//                sign-out. Nothing is written, because writing it back to
//                where it came from is at best a no-op and at worst copies
//                one person's account setting onto a shared machine.
//
// Collapsing them into one setter is the mistake to avoid: applying a fetched
// preference through `setLocale` would immediately persist it again, which is
// how an account choice leaks into the browser of whoever borrowed the login.
// ═══════════════════════════════════════════════════════════════════

/** `{name}` → value. Numbers are formatted for the locale; strings pass through. */
export type Vars = Record<string, string | number>;

export interface LocaleContextValue {
  locale: Locale;
  /**
   * Somebody chose this language.
   *
   * STORED BEFORE IT IS SHOWN: the write is awaited, then the state moves, so
   * nothing is ever displayed as the current preference before an attempt was
   * made to keep it. The switch happens even when that attempt failed — an
   * unreachable server or a browser that throws on `localStorage` is a
   * preference that will not follow you, not a language you may not read in.
   */
  setLocale: (next: Locale) => Promise<void>;
  /**
   * Apply a preference that came from somewhere that already has it — the
   * account, on sign-in; this browser, on sign-out. Writes nothing back.
   */
  adoptLocale: (next: Locale) => void;
  t: (key: MessageKey, vars?: Vars) => string;
  /** A date, formatted for display. NEVER for a storage key — see below. */
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Resolve one message.
 *
 * THE FALLBACK IS NORWEGIAN, then the key itself. A missing translation shows
 * the source language, which is readable and obviously untranslated; showing
 * the KEY would be neither. The key is the last resort so a typo is visible in
 * development rather than rendering as empty space.
 */
export function translate(locale: Locale, key: MessageKey, vars?: Vars, tag?: string): string {
  const catalog = CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
  const template = catalog?.[key] ?? nb[key] ?? key;
  if (!vars) return template;
  // `{name}` only. No expressions, no nesting: a catalog is data, and a
  // template language inside it is a way to put logic where nobody looks.
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    if (value === undefined) return whole; // leave it visible rather than blank
    return typeof value === 'number'
      ? new Intl.NumberFormat(tag ?? INTL_TAG[locale]).format(value)
      : String(value);
  });
}

export function LocaleProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** Tests and stories pass this; the app reads the stored preference. */
  initial?: Locale;
}) {
  // THE BROWSER'S VALUE IS THE STARTING POINT, ALWAYS. It is the only one
  // available before anybody has signed in, and the login screen is a
  // translated surface. An account's preference arrives later, through
  // `adoptLocale`, because learning it requires a session.
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? readLocale());

  const setLocale = useCallback(async (next: Locale) => {
    // THE RULE: persistence is ATTEMPTED AND AWAITED before the UI switches,
    // and a failed attempt does not block the session-local change.
    //
    // Awaited first, because storage-first is the house rule and because the
    // seam decides where the value goes — the account when there is one, this
    // browser otherwise (localePreference.ts).
    //
    // The catch makes the second half structural rather than borrowed.
    // `persistLocale` is written not to reject, but that is another module's
    // contract: if it ever did, an un-caught rejection here would leave
    // somebody stuck in a language they cannot read — inside a dropdown
    // handler, where nothing is watching. What a failure actually costs is
    // only that the choice does not follow them to the next machine, and that
    // is not worth trapping them for.
    //
    // HONESTLY: this catch is DEFENCE IN DEPTH AND CANNOT BE OBSERVED while
    // the callee also catches. Removing it fails no test, and that is not an
    // oversight in the suite — it is what «two layers» means. The behaviour
    // itself is pinned end to end in `i18n-account-locale.test.tsx` («a
    // failed persist does not trap anyone in a language»), which is the level
    // that matters; this line is here for the day somebody simplifies the
    // seam below and does not read this far.
    try {
      await persistLocale(next);
    } catch (e) {
      // Swallowed, not silent: an outage should be visible to whoever is
      // looking at a console, the same shape `recordAuditEvent` uses.
      console.error('[i18n] the language preference was not stored:', e);
    }
    setLocaleState(next);
  }, []);

  // Deliberately NOT routed through `setLocale`: a preference we were told
  // about must not be written back to where it came from.
  const adoptLocale = useCallback((next: Locale) => setLocaleState(next), []);

  // ── THE DOCUMENT'S LANGUAGE FOLLOWS THE LOCALE (#781) ──────────────
  //
  // `index.html` hard-codes `lang="nb"` and nothing ever changed it, so
  // switching to English left the page still DECLARED as Norwegian — WCAG
  // 3.1.1, Level A, and the failure is audible: a screen reader goes on
  // pronouncing English text with Norwegian phonetics, which is worse than
  // unhelpful.
  //
  // The initial value in the markup stays as it is and is correct: `nb` is the
  // default locale, so the first paint is already right and this only has to
  // follow a change.
  //
  // `INTL_TAG` rather than the bare locale, because `lang` wants a BCP-47 tag
  // and that map is already the place this app decides what each locale's tag
  // is — `formatDate` and `Intl.NumberFormat` read the same one, so they
  // cannot drift apart.
  useEffect(() => {
    document.documentElement.lang = INTL_TAG[locale];
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => {
    const tag = INTL_TAG[locale];
    return {
      locale,
      setLocale,
      adoptLocale,
      t: (key, vars) => translate(locale, key, vars, tag),
      // DISPLAY ONLY. `docs/architecture/DOMAIN_RULES.md` §4 requires local
      // dates built by hand for anything that becomes a key or a stored
      // value; a localized string is the opposite of that and must never be
      // parsed back or written down.
      formatDate: (v, options) =>
        new Intl.DateTimeFormat(tag, options ?? { dateStyle: 'medium' }).format(
          typeof v === 'number' ? new Date(v) : v,
        ),
      formatNumber: (v, options) => new Intl.NumberFormat(tag, options).format(v),
    };
  }, [locale, setLocale, adoptLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * The hook every migrated surface uses.
 *
 * Throws outside a provider rather than falling back to Norwegian: a silent
 * default would make a missing provider invisible until someone switched
 * language and half the screen did not move.
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale() used outside <LocaleProvider>');
  return ctx;
}

/** The common case: just the translator. */
export function useT(): LocaleContextValue['t'] {
  return useLocale().t;
}
